import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  Building2, MapPin, Calendar, Package, Globe, Handshake, Award,
  User, Mail, Phone, Link as LinkIcon, Video, Youtube, Upload,
  ArrowRight, ArrowLeft, Check, Sparkles, Loader2, Eye, Share2, Image, X, MessageSquare, Lock, Crown, Shield
} from 'lucide-react';

import PlanWizard, { planTotal } from '../components/PlanWizard';
import PlanReview from '../components/PlanReview';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PLAN_STORAGE_KEY = 'bs_profile_plan';

const loadRazorpay = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  const s = document.createElement('script');
  s.src = 'https://checkout.razorpay.com/v1/checkout.js';
  s.onload = () => resolve(true);
  s.onerror = () => resolve(false);
  document.body.appendChild(s);
});

// Step indicator component
function StepIndicator({ currentStep, steps }) {
  return (
    <div className="flex items-center justify-center mb-8">
      {steps.map((step, index) => (
        <div key={index} className="flex items-center">
          <div className={`
            w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm
            ${index < currentStep ? 'bg-[#C41E3A] text-white' : 
              index === currentStep ? 'bg-[#C41E3A] text-white ring-4 ring-[#C41E3A]/30' : 
              'bg-zinc-800 text-zinc-500'}
          `}>
            {index < currentStep ? <Check className="w-5 h-5" /> : index + 1}
          </div>
          {index < steps.length - 1 && (
            <div className={`w-16 h-1 mx-2 ${index < currentStep ? 'bg-[#C41E3A]' : 'bg-zinc-800'}`} />
          )}
        </div>
      ))}
    </div>
  );
}

export default function ProfileCreate() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingContactBio, setIsGeneratingContactBio] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isUploadingContactPhoto, setIsUploadingContactPhoto] = useState(false);
  const [error, setError] = useState('');
  const logoInputRef = useRef(null);
  const contactPhotoInputRef = useRef(null);
  
  // Check if user is admin (full access) or has paid tier
  const isAdmin = user?.email?.toLowerCase() === 'brandstagevideo@gmail.com';
  const hasPaidTier = true; // All profile-builder features are part of Basic; Pro features are sold as add-ons
  
  // Plan journey state
  const [journey, setJourney] = useState(isAdmin ? 'form' : 'wizard');
  const [pricing, setPricing] = useState(null);
  const [addons, setAddons] = useState([]);
  const [plan, setPlanState] = useState(() => {
    try { return JSON.parse(localStorage.getItem(PLAN_STORAGE_KEY)) || { product_type: 'basic_1y', addons: [] }; } catch { return { product_type: 'basic_1y', addons: [] }; }
  });
  const setPlan = (p) => { setPlanState(p); localStorage.setItem(PLAN_STORAGE_KEY, JSON.stringify(p)); };
  const [publishDeclaration, setPublishDeclaration] = useState(false);
  const [paymentTerms, setPaymentTerms] = useState(false);
  const [promoCode, setPromoCode] = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/api/payments/pricing`).then((r) => setPricing(r.data)).catch(() => {});
    axios.get(`${API_URL}/api/addons`).then((r) => setAddons(r.data.addons || [])).catch(() => {});
  }, []);

  // Upgrade modal state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState('');
  
  // Show upgrade prompt for non-paid users
  const requirePaidTier = (featureName) => {
    if (hasPaidTier) return true;
    setUpgradeFeature(featureName);
    setShowUpgradeModal(true);
    return false;
  };
  
  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [couponStatus, setCouponStatus] = useState(null); // null, 'validating', 'valid', 'invalid', 'redeemed'
  const [couponData, setCouponData] = useState(null);
  const [couponError, setCouponError] = useState('');
  
  // Validate coupon
  const validateCoupon = async () => {
    if (!couponCode.trim()) return;
    
    setCouponStatus('validating');
    setCouponError('');
    
    try {
      const response = await axios.post(`${API_URL}/api/coupons/validate`, 
        { code: couponCode.trim() },
        { withCredentials: true }
      );
      setCouponData(response.data);
      setCouponStatus('valid');
    } catch (err) {
      setCouponError(err.response?.data?.detail || 'Invalid coupon code');
      setCouponStatus('invalid');
      setCouponData(null);
    }
  };
  
  // Redeem coupon (for free upgrades)
  const redeemCoupon = async () => {
    if (!couponCode.trim() || couponStatus !== 'valid') return;
    
    setCouponStatus('validating');
    
    try {
      const response = await axios.post(`${API_URL}/api/coupons/redeem`,
        { code: couponCode.trim() },
        { withCredentials: true }
      );
      
      if (response.data.upgraded) {
        setCouponStatus('redeemed');
        // Refresh page to update user state
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else if (response.data.requires_payment) {
        // Partial discount - would need payment integration
        alert(`Coupon applied! Final price: ₹${response.data.final_price}. Payment integration coming soon.`);
      }
    } catch (err) {
      setCouponError(err.response?.data?.detail || 'Failed to redeem coupon');
      setCouponStatus('invalid');
    }
  };
  
  // Form data
  const [formData, setFormData] = useState({
    // Step 1: Basics
    company_name: '',
    tagline: '',
    year_established: '',
    headquarters: '',
    
    // Step 2: Objective & Audience (NEW)
    profile_category: 'general',
    custom_category: '',
    target_audience: '',
    key_message: '',
    
    // Step 3: Details
    products_services: '',
    key_markets: '',
    looking_for: '',
    certifications: [],
    
    // KPI Stats
    products_count: '',
    clients_count: '',
    countries_count: '',
    
    // Step 4: Media
    logo_url: '',
    hero_video: null,
    additional_videos: [],
    
    // Contact (basic)
    contact_name: '',
    contact_designation: '',
    contact_email: user?.email || '',
    contact_phone: '',
    website: '',
    
    // Key Contact Profile (optional)
    include_key_contact: false,
    key_contact: {
      photo_url: '',
      years_experience: '',
      expertise_areas: [],
      achievements: '',
      linkedin_url: '',
      bio_text: '',  // AI-generated professional intro
      videos: []     // Contact's personal videos
    },
    
    // Admin field
    created_on_behalf_of: '',
    
    // Generated structured sections (NEW - replaces single profile_text)
    profile_sections: null,  // { who_we_are, our_presence, what_we_seek, why_partner, call_to_action }
    audience_messages: [],   // AI-generated messages for target audience
    
    // Optional extended sections
    company_snapshot: '',
    leadership_section: '',
    products_section: '',
    case_studies_section: '',
    
    // Legacy - for backwards compatibility
    profile_text: ''
  });
  
  const steps = ['Basics', 'Objective', 'Details', 'Media', 'Preview'];
  
  // Profile categories - general is free, others are premium
  const profileCategories = [
    { id: 'general', label: 'General Profile', description: 'Standard company profile', free: true },
    { id: 'business_expansion', label: 'Business Expansion', description: 'Overseas company entering new market', free: false },
    { id: 'international_market', label: 'International Market Development', description: 'Seeking global customers', free: false },
    { id: 'investment_invitation', label: 'Investment Invitation', description: 'Inviting investors/funding', free: false },
    { id: 'trade_show', label: 'Trade Show / Event', description: 'Event-specific profile', free: false },
    { id: 'partnership', label: 'Partnership Seeking', description: 'Looking for JV/distribution partners', free: false },
    { id: 'announcement', label: 'Announcement', description: 'Company news, visits, events, or good news', free: false },
    { id: 'other', label: 'Other', description: 'Custom category', free: false }
  ];
  
  // Premium features list for upgrade modal
  const premiumFeatures = [
    'All 8 profile categories (Business Expansion, Trade Show, etc.)',
    'AI-powered structured profile sections',
    'AI-generated audience messaging',
    'Key Contact profile with AI bio',
    'Video embedding (YouTube + BrandStage)',
    'Certification badges',
    'Unlimited edits'
  ];
  
  // Certification options
  const certificationOptions = [
    'WHO-GMP', 'ISO 9001', 'ISO 14001', 'ISO 22000', 'FDA Approved',
    'CE Marked', 'HACCP', 'GMP', 'NABL', 'BIS', 'FSSAI', 'Halal', 'Kosher'
  ];
  
  // Expertise area options
  const expertiseOptions = [
    'International Trade', 'Export Management', 'Business Development', 'Strategic Partnerships',
    'Regulatory Compliance', 'Supply Chain', 'Manufacturing', 'Quality Assurance',
    'R&D', 'Sales & Marketing', 'Finance', 'Operations', 'Technology', 'Human Resources'
  ];
  
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };
  
  const toggleCertification = (cert) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev.certifications.includes(cert)
        ? prev.certifications.filter(c => c !== cert)
        : [...prev.certifications, cert]
    }));
  };
  
  const toggleExpertise = (expertise) => {
    setFormData(prev => ({
      ...prev,
      key_contact: {
        ...prev.key_contact,
        expertise_areas: prev.key_contact.expertise_areas.includes(expertise)
          ? prev.key_contact.expertise_areas.filter(e => e !== expertise)
          : [...prev.key_contact.expertise_areas, expertise]
      }
    }));
  };
  
  const updateKeyContact = (field, value) => {
    setFormData(prev => ({
      ...prev,
      key_contact: { ...prev.key_contact, [field]: value }
    }));
  };
  
  const addKeyContactVideo = () => {
    if (formData.key_contact.videos.length < 3) {
      setFormData(prev => ({
        ...prev,
        key_contact: {
          ...prev.key_contact,
          videos: [...prev.key_contact.videos, { type: 'youtube', url: '', title: '' }]
        }
      }));
    }
  };
  
  const updateKeyContactVideo = (index, field, value) => {
    setFormData(prev => {
      const videos = [...prev.key_contact.videos];
      videos[index] = { ...videos[index], [field]: value };
      return { ...prev, key_contact: { ...prev.key_contact, videos } };
    });
  };
  
  const removeKeyContactVideo = (index) => {
    setFormData(prev => ({
      ...prev,
      key_contact: {
        ...prev.key_contact,
        videos: prev.key_contact.videos.filter((_, i) => i !== index)
      }
    }));
  };
  
  const handleContactPhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file (PNG, JPG, etc.)');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      setError('Photo file must be less than 5MB');
      return;
    }
    
    setIsUploadingContactPhoto(true);
    setError('');
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('upload_preset', 'ml_default');
      
      const response = await axios.post(
        `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'demo'}/image/upload`,
        formDataUpload
      );
      
      if (response.data.secure_url) {
        updateKeyContact('photo_url', response.data.secure_url);
      }
    } catch (err) {
      console.error('Photo upload failed:', err);
      const reader = new FileReader();
      reader.onloadend = () => {
        updateKeyContact('photo_url', reader.result);
      };
      reader.readAsDataURL(file);
    } finally {
      setIsUploadingContactPhoto(false);
    }
  };
  
  const removeContactPhoto = () => {
    updateKeyContact('photo_url', '');
    if (contactPhotoInputRef.current) {
      contactPhotoInputRef.current.value = '';
    }
  };
  
  const generateContactBio = async () => {
    setIsGeneratingContactBio(true);
    setError('');
    
    try {
      const response = await axios.post(`${API_URL}/api/profiles/generate-contact-bio`, {
        name: formData.contact_name,
        designation: formData.contact_designation,
        company_name: formData.company_name,
        years_experience: formData.key_contact.years_experience,
        expertise_areas: formData.key_contact.expertise_areas.join(', '),
        achievements: formData.key_contact.achievements
      }, { withCredentials: true });
      
      updateKeyContact('bio_text', response.data.bio_text);
    } catch (err) {
      setError('Failed to generate bio. Please try again.');
      console.error(err);
    } finally {
      setIsGeneratingContactBio(false);
    }
  };
  
  const generateProfileText = async () => {
    // AI generation is a premium feature for non-general categories
    if (!hasPaidTier && formData.profile_category !== 'general') {
      requirePaidTier('AI Profile Generation');
      return;
    }
    
    setIsGenerating(true);
    setError('');
    
    try {
      // Use new structured profile generation
      const response = await axios.post(`${API_URL}/api/profiles/generate-structured`, {
        company_name: formData.company_name,
        tagline: formData.tagline,
        year_established: formData.year_established,
        headquarters: formData.headquarters,
        products_services: formData.products_services,
        key_markets: formData.key_markets,
        looking_for: formData.looking_for,
        certifications: formData.certifications.join(', '),
        profile_category: formData.profile_category,
        custom_category: formData.custom_category,
        target_audience: formData.target_audience,
        key_message: formData.key_message
      }, { withCredentials: true });
      
      setFormData(prev => ({ 
        ...prev, 
        profile_sections: response.data.sections,
        audience_messages: response.data.audience_messages || [],
        // Also generate legacy profile_text for backwards compatibility
        profile_text: Object.values(response.data.sections || {}).filter(Boolean).join('\n\n')
      }));
    } catch (err) {
      setError('Failed to generate profile. Please try again.');
      console.error(err);
    } finally {
      setIsGenerating(false);
    }
  };
  
  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file (PNG, JPG, etc.)');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Logo file must be less than 5MB');
      return;
    }
    
    setIsUploadingLogo(true);
    setError('');
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('upload_preset', 'ml_default'); // Cloudinary unsigned preset
      
      // Upload to Cloudinary
      const response = await axios.post(
        `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'demo'}/image/upload`,
        formDataUpload
      );
      
      if (response.data.secure_url) {
        setFormData(prev => ({ ...prev, logo_url: response.data.secure_url }));
      }
    } catch (err) {
      console.error('Logo upload failed:', err);
      // Fallback: convert to base64 data URL for preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, logo_url: reader.result }));
      };
      reader.readAsDataURL(file);
    } finally {
      setIsUploadingLogo(false);
    }
  };
  
  const removeLogo = () => {
    setFormData(prev => ({ ...prev, logo_url: '' }));
    if (logoInputRef.current) {
      logoInputRef.current.value = '';
    }
  };
  
  const addVideoSlot = () => {
    if (formData.additional_videos.length < 5) {
      setFormData(prev => ({
        ...prev,
        additional_videos: [...prev.additional_videos, { type: 'youtube', url: '', title: '' }]
      }));
    }
  };
  
  const updateVideo = (index, field, value) => {
    setFormData(prev => {
      const videos = [...prev.additional_videos];
      videos[index] = { ...videos[index], [field]: value };
      return { ...prev, additional_videos: videos };
    });
  };
  
  const removeVideo = (index) => {
    setFormData(prev => ({
      ...prev,
      additional_videos: prev.additional_videos.filter((_, i) => i !== index)
    }));
  };
  
  const validateStep = () => {
    switch (currentStep) {
      case 0: // Basics
        if (!formData.company_name.trim()) {
          setError('Company name is required');
          return false;
        }
        break;
      case 1: // Objective & Audience
        if (!formData.target_audience.trim()) {
          setError('Target audience is required');
          return false;
        }
        if (!formData.key_message.trim()) {
          setError('Key message is required');
          return false;
        }
        if (formData.profile_category === 'other' && !formData.custom_category.trim()) {
          setError('Please describe your custom category');
          return false;
        }
        break;
      case 2: // Details
        if (!formData.products_services.trim()) {
          setError('Products/Services description is required');
          return false;
        }
        if (!formData.contact_name.trim() || !formData.contact_email.trim()) {
          setError('Contact name and email are required');
          return false;
        }
        break;
      case 3: // Media
        // Media is optional
        break;
      case 4: // Preview
        if (!formData.profile_sections && !formData.profile_text.trim()) {
          setError('Please generate profile content');
          return false;
        }
        break;
    }
    setError('');
    return true;
  };
  
  const nextStep = async () => {
    if (!validateStep()) return;
    
    // Generate structured profile when moving to preview (step 4)
    if (currentStep === 3 && !formData.profile_sections) {
      await generateProfileText();
    }
    
    setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
  };
  
  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };
  
  const payForPlan = async (profile) => {
    const goToProfile = (msg) => { if (msg) alert(msg); navigate(`/p/${profile.slug}`); };
    try {
      if (!(await loadRazorpay())) return goToProfile('Your profile is saved as a private draft. The payment page could not load. You can pay from your profile page.');
      const { data } = await axios.post(`${API_URL}/api/payments/create-order`, {
        product_type: plan.product_type,
        addons: plan.product_type === 'all_access_1y' ? [] : plan.addons,
        country: (user?.country || 'india').toLowerCase() === 'india' ? 'india' : 'international',
        coupon_code: promoCode.trim() || null,
        terms_accepted: true,
      }, { withCredentials: true });
      const rzp = new window.Razorpay({
        key: data.razorpay_key_id, amount: data.amount, currency: data.currency, name: 'BrandStage',
        description: data.product_name, order_id: data.order_id, prefill: data.prefill, theme: { color: '#C41E3A' },
        handler: async (resp) => {
          try {
            await axios.post(`${API_URL}/api/payments/verify`, resp, { withCredentials: true });
            localStorage.removeItem(PLAN_STORAGE_KEY);
            goToProfile();
          } catch {
            goToProfile('Payment received but verification failed. Our team will activate your profile shortly.');
          }
        },
        modal: { ondismiss: () => goToProfile('Your profile is saved as a private draft. Only you can see it until payment is complete.') },
      });
      rzp.open();
    } catch (err) {
      goToProfile(`Your profile is saved as a private draft. ${err.response?.data?.detail || 'Payment could not start.'}`);
    }
  };

  const submitProfile = async (pay = false) => {
    if (!validateStep()) return;
    if (!isAdmin && !publishDeclaration) {
      setError('Please confirm the accuracy & authority declaration before continuing');
      return;
    }
    if (pay && !paymentTerms) {
      setError('Please accept the plan terms to continue to payment');
      return;
    }
    
    setIsSubmitting(true);
    setError('');
    
    try {
      // Filter out empty videos
      const validVideos = formData.additional_videos.filter(v => v.url.trim());
      const validKeyContactVideos = formData.key_contact.videos.filter(v => v.url.trim());
      
      // Prepare key_contact data only if enabled
      const keyContactData = formData.include_key_contact ? {
        photo_url: formData.key_contact.photo_url,
        years_experience: formData.key_contact.years_experience,
        expertise_areas: formData.key_contact.expertise_areas,
        achievements: formData.key_contact.achievements,
        linkedin_url: formData.key_contact.linkedin_url,
        bio_text: formData.key_contact.bio_text,
        videos: validKeyContactVideos
      } : null;
      
      const response = await axios.post(`${API_URL}/api/profiles`, {
        company_name: formData.company_name,
        tagline: formData.tagline,
        logo_url: formData.logo_url,
        
        // Profile Category & Objective (NEW)
        profile_category: formData.profile_category,
        custom_category: formData.custom_category,
        target_audience: formData.target_audience,
        key_message: formData.key_message,
        
        // Structured sections (NEW)
        profile_sections: formData.profile_sections,
        audience_messages: formData.audience_messages,
        
        // Legacy text for backwards compatibility
        profile_text: formData.profile_text,
        
        year_established: formData.year_established,
        headquarters: formData.headquarters,
        products_services: formData.products_services,
        key_markets: formData.key_markets,
        looking_for: formData.looking_for,
        certifications: formData.certifications,
        
        // KPI Stats
        products_count: formData.products_count ? parseInt(formData.products_count) : null,
        clients_count: formData.clients_count ? parseInt(formData.clients_count) : null,
        countries_count: formData.countries_count || null,  // Can be number or text
        
        hero_video: formData.hero_video,
        additional_videos: validVideos,
        contact_name: formData.contact_name,
        contact_designation: formData.contact_designation,
        contact_email: formData.contact_email,
        contact_phone: formData.contact_phone,
        website: formData.website,
        created_on_behalf_of: formData.created_on_behalf_of,
        key_contact: keyContactData,
        publish_declaration: isAdmin || publishDeclaration,
        selected_plan: plan
      }, { withCredentials: true });
      
      if (pay) {
        await payForPlan(response.data);
      } else {
        navigate(`/p/${response.data.slug}`);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create profile. Please try again.');
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Redirect if not logged in
  if (!user) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center p-6">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 max-w-md text-center">
          <Building2 className="w-12 h-12 text-[#C41E3A] mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Create Your Profile</h2>
          <p className="text-zinc-400 mb-6">Sign in or create an account to build your company profile.</p>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/login', { state: { from: '/profile/create' } })}
              className="w-full bg-[#C41E3A] hover:bg-[#A01830] text-white px-6 py-3 rounded-lg font-semibold"
            >
              Log In
            </button>
            <button
              onClick={() => navigate('/register', { state: { from: '/profile/create' } })}
              className="w-full border border-zinc-700 hover:border-zinc-500 text-white px-6 py-3 rounded-lg font-semibold"
            >
              Create Account
            </button>
          </div>
          <p className="text-zinc-500 text-sm mt-4">Build and preview free, pay only to publish.</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[#09090B] py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <p className="text-xs tracking-[0.3em] uppercase text-[#C41E3A] font-bold mb-2">
            Create Profile
          </p>
          <h1 className="text-3xl md:text-4xl font-black text-white tracking-tight">
            Professional Business Profile
          </h1>
          <p className="text-zinc-400 mt-2">
            Build your shareable company profile in minutes
          </p>
          
          {/* Profile Type Switch */}
          <div className="mt-4 flex items-center justify-center gap-2 text-sm">
            <span className="text-zinc-500">Looking to create a personal profile?</span>
            <a 
              href="/leadership/create" 
              className="text-[#C41E3A] hover:text-[#ff4d6a] font-medium"
            >
              Create Leadership Profile →
            </a>
          </div>
        </div>
        
        {journey === 'wizard' && pricing && addons.length > 0 ? (
          <PlanWizard pricing={pricing} addons={addons} plan={plan} setPlan={setPlan} onDone={() => { setJourney('form'); window.scrollTo(0, 0); }} />
        ) : (
        <>
        {/* Step Indicator */}
        <StepIndicator currentStep={currentStep} steps={steps} />
        
        {/* Error Message */}
        {error && (
          <div className="bg-red-900/30 border border-red-800 text-red-400 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}
        
        {/* Form Steps */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 md:p-8">
          
          {/* Step 1: Basics */}
          {currentStep === 0 && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#C41E3A]" />
                Company Basics
              </h2>
              
              {/* Private profile reassurance */}
              <div className="flex items-start gap-2.5 bg-zinc-900/60 border border-zinc-800 rounded-lg px-4 py-3">
                <Shield className="w-4 h-4 text-[#C41E3A] mt-0.5 shrink-0" />
                <p className="text-zinc-400 text-sm leading-relaxed">
                  Your profile is <span className="text-white font-medium">private by design</span>. Only people you share the link with can see it — it won't appear in search engines or public directories.
                </p>
              </div>
              
              <div>
                <label className="block text-zinc-300 text-sm font-medium mb-2">
                  Company Name *
                </label>
                <input
                  type="text"
                  value={formData.company_name}
                  onChange={(e) => handleInputChange('company_name', e.target.value)}
                  placeholder="e.g., Meridian Pharmaceuticals"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A]"
                  data-testid="profile-company-name"
                />
              </div>
              
              <div>
                <label className="block text-zinc-300 text-sm font-medium mb-2">
                  Tagline
                </label>
                <input
                  type="text"
                  value={formData.tagline}
                  onChange={(e) => handleInputChange('tagline', e.target.value)}
                  placeholder="e.g., Excellence in API Manufacturing"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A]"
                  data-testid="profile-tagline"
                />
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-zinc-300 text-sm font-medium mb-2">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    Year Established
                  </label>
                  <input
                    type="text"
                    value={formData.year_established}
                    onChange={(e) => handleInputChange('year_established', e.target.value)}
                    placeholder="e.g., 1992"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A]"
                    data-testid="profile-year"
                  />
                </div>
                <div>
                  <label className="block text-zinc-300 text-sm font-medium mb-2">
                    <MapPin className="w-4 h-4 inline mr-1" />
                    Headquarters
                  </label>
                  <input
                    type="text"
                    value={formData.headquarters}
                    onChange={(e) => handleInputChange('headquarters', e.target.value)}
                    placeholder="e.g., Mumbai, India"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A]"
                    data-testid="profile-headquarters"
                  />
                </div>
              </div>
              
              {/* Admin-only field */}
              {isAdmin && (
                <div className="border-t border-zinc-700 pt-6 mt-6">
                  <label className="block text-zinc-300 text-sm font-medium mb-2">
                    <Handshake className="w-4 h-4 inline mr-1" />
                    Created on Behalf of (Agency/Trade Mission)
                  </label>
                  <input
                    type="text"
                    value={formData.created_on_behalf_of}
                    onChange={(e) => handleInputChange('created_on_behalf_of', e.target.value)}
                    placeholder="e.g., Ghana Pharma Trade Delegation 2026"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A]"
                    data-testid="profile-on-behalf"
                  />
                  <p className="text-zinc-500 text-xs mt-1">
                    Leave blank if creating for direct client
                  </p>
                </div>
              )}
            </div>
          )}
          
          {/* Step 2: Objective & Audience (NEW) */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#C41E3A]" />
                Profile Objective & Audience
              </h2>
              <p className="text-zinc-400 text-sm">
                Define the purpose of this profile and who you want to reach. This helps us generate targeted messaging.
              </p>
              
              {/* Profile Category */}
              <div>
                <label className="block text-zinc-300 text-sm font-medium mb-3">
                  What type of profile is this? *
                </label>
                <div className="grid gap-3">
                  {profileCategories.map(cat => {
                    const isPremium = !cat.free && !hasPaidTier;
                    return (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => {
                          if (isPremium) {
                            requirePaidTier('Profile Categories');
                          } else {
                            handleInputChange('profile_category', cat.id);
                          }
                        }}
                        className={`text-left p-4 rounded-lg border transition-all relative ${
                          formData.profile_category === cat.id
                            ? 'bg-[#C41E3A]/20 border-[#C41E3A] ring-2 ring-[#C41E3A]/30'
                            : isPremium 
                              ? 'bg-zinc-800/30 border-zinc-700/50 opacity-75 hover:opacity-100'
                              : 'bg-zinc-800/50 border-zinc-700 hover:border-zinc-500'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="font-medium text-white">{cat.label}</div>
                          {!cat.free && (
                            <span className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 ${
                              hasPaidTier 
                                ? 'bg-emerald-500/20 text-emerald-400' 
                                : 'bg-amber-500/20 text-amber-400'
                            }`}>
                              {hasPaidTier ? <Check className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                              {hasPaidTier ? 'Included' : 'Pro'}
                            </span>
                          )}
                          {cat.free && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-zinc-700 text-zinc-400">
                              Free
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-zinc-400 mt-1">{cat.description}</div>
                      </button>
                    );
                  })}
                </div>
                {!hasPaidTier && (
                  <p className="text-amber-400/80 text-xs mt-3 flex items-center gap-1">
                    <Crown className="w-3 h-3" />
                    Upgrade to Pro to unlock all profile categories with AI-powered messaging
                  </p>
                )}
              </div>
              
              {/* Custom Category (if "other" selected) */}
              {formData.profile_category === 'other' && (
                <div>
                  <label className="block text-zinc-300 text-sm font-medium mb-2">
                    Describe your profile category
                  </label>
                  <input
                    type="text"
                    value={formData.custom_category}
                    onChange={(e) => handleInputChange('custom_category', e.target.value)}
                    placeholder="e.g., Pharmaceutical distributor seeking Africa expansion"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A]"
                  />
                </div>
              )}
              
              {/* Target Audience */}
              <div className="pt-4 border-t border-zinc-700">
                <label className="block text-zinc-300 text-sm font-medium mb-2">
                  <User className="w-4 h-4 inline mr-1" />
                  Who is your target audience? *
                </label>
                <input
                  type="text"
                  value={formData.target_audience}
                  onChange={(e) => handleInputChange('target_audience', e.target.value)}
                  placeholder="e.g., Pharmaceutical distributors in Southeast Asia, Healthcare investors, Trade show visitors"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A]"
                  data-testid="profile-target-audience"
                />
                <p className="text-zinc-500 text-xs mt-1">
                  Be specific - the more focused, the better the messaging
                </p>
              </div>
              
              {/* Key Message */}
              <div>
                <label className="block text-zinc-300 text-sm font-medium mb-2">
                  What&apos;s your key message for this audience? *
                </label>
                <textarea
                  value={formData.key_message}
                  onChange={(e) => handleInputChange('key_message', e.target.value)}
                  placeholder="e.g., We're expanding to India and seeking distribution partners for our WHO-GMP certified products"
                  rows={3}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A] resize-none"
                  data-testid="profile-key-message"
                />
                <p className="text-zinc-500 text-xs mt-1">
                  What do you want them to know or do after viewing your profile?
                </p>
              </div>
            </div>
          )}
          
          {/* Step 3: Details */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Package className="w-5 h-5 text-[#C41E3A]" />
                Company Details
              </h2>
              
              <div>
                <label className="block text-zinc-300 text-sm font-medium mb-2">
                  Products / Services *
                </label>
                <textarea
                  value={formData.products_services}
                  onChange={(e) => handleInputChange('products_services', e.target.value)}
                  placeholder="Describe what your company offers..."
                  rows={3}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A] resize-none"
                  data-testid="profile-products"
                />
              </div>
              
              <div>
                <label className="block text-zinc-300 text-sm font-medium mb-2">
                  <Globe className="w-4 h-4 inline mr-1" />
                  Key Markets / Regions
                </label>
                <input
                  type="text"
                  value={formData.key_markets}
                  onChange={(e) => handleInputChange('key_markets', e.target.value)}
                  placeholder="e.g., Africa, Southeast Asia, Europe"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A]"
                  data-testid="profile-markets"
                />
              </div>
              
              <div>
                <label className="block text-zinc-300 text-sm font-medium mb-2">
                  <Handshake className="w-4 h-4 inline mr-1" />
                  Looking For
                </label>
                <input
                  type="text"
                  value={formData.looking_for}
                  onChange={(e) => handleInputChange('looking_for', e.target.value)}
                  placeholder="e.g., Distribution partners, Strategic alliances"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A]"
                  data-testid="profile-looking-for"
                />
              </div>
              
              {/* KPI Stats */}
              <div className="border-t border-zinc-700 pt-6 mt-6">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Globe className="w-5 h-5 text-[#C41E3A]" />
                  Company Stats (Optional)
                </h3>
                <p className="text-zinc-400 text-sm mb-4">These numbers will be displayed as highlight cards on your profile</p>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-zinc-300 text-sm font-medium mb-2">
                      Products / SKUs
                    </label>
                    <input
                      type="number"
                      value={formData.products_count}
                      onChange={(e) => handleInputChange('products_count', e.target.value)}
                      placeholder="e.g., 150"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A]"
                      data-testid="profile-products-count"
                    />
                  </div>
                  <div>
                    <label className="block text-zinc-300 text-sm font-medium mb-2">
                      Clients Served
                    </label>
                    <input
                      type="number"
                      value={formData.clients_count}
                      onChange={(e) => handleInputChange('clients_count', e.target.value)}
                      placeholder="e.g., 500"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A]"
                      data-testid="profile-clients-count"
                    />
                  </div>
                  <div>
                    <label className="block text-zinc-300 text-sm font-medium mb-2">
                      Countries / Markets
                    </label>
                    <input
                      type="text"
                      value={formData.countries_count}
                      onChange={(e) => handleInputChange('countries_count', e.target.value)}
                      placeholder="e.g., 25 or India, Thailand, Vietnam"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A]"
                      data-testid="profile-countries-count"
                    />
                    <p className="text-zinc-500 text-xs mt-1">Enter a number or list country names</p>
                  </div>
                </div>
              </div>
              
              <div>
                <label className="block text-zinc-300 text-sm font-medium mb-2">
                  <Award className="w-4 h-4 inline mr-1" />
                  Certifications
                </label>
                <div className="flex flex-wrap gap-2">
                  {certificationOptions.map(cert => (
                    <button
                      key={cert}
                      type="button"
                      onClick={() => toggleCertification(cert)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                        formData.certifications.includes(cert)
                          ? 'bg-[#C41E3A] text-white'
                          : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                      }`}
                    >
                      {cert}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Contact Info */}
              <div className="border-t border-zinc-700 pt-6 mt-6">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-[#C41E3A]" />
                  Contact Information
                </h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-zinc-300 text-sm font-medium mb-2">
                      Contact Name *
                    </label>
                    <input
                      type="text"
                      value={formData.contact_name}
                      onChange={(e) => handleInputChange('contact_name', e.target.value)}
                      placeholder="e.g., Rajesh Kumar"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A]"
                      data-testid="profile-contact-name"
                    />
                  </div>
                  <div>
                    <label className="block text-zinc-300 text-sm font-medium mb-2">
                      Designation
                    </label>
                    <input
                      type="text"
                      value={formData.contact_designation}
                      onChange={(e) => handleInputChange('contact_designation', e.target.value)}
                      placeholder="e.g., Director - International Business"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A]"
                      data-testid="profile-designation"
                    />
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-zinc-300 text-sm font-medium mb-2">
                      <Mail className="w-4 h-4 inline mr-1" />
                      Email *
                    </label>
                    <input
                      type="email"
                      value={formData.contact_email}
                      onChange={(e) => handleInputChange('contact_email', e.target.value)}
                      placeholder="e.g., exports@company.com"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A]"
                      data-testid="profile-email"
                    />
                  </div>
                  <div>
                    <label className="block text-zinc-300 text-sm font-medium mb-2">
                      <Phone className="w-4 h-4 inline mr-1" />
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={formData.contact_phone}
                      onChange={(e) => handleInputChange('contact_phone', e.target.value)}
                      placeholder="e.g., +91 98765 43210"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A]"
                      data-testid="profile-phone"
                    />
                  </div>
                </div>
                
                <div className="mt-4">
                  <label className="block text-zinc-300 text-sm font-medium mb-2">
                    <LinkIcon className="w-4 h-4 inline mr-1" />
                    Website
                  </label>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                    placeholder="e.g., https://www.company.com"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A]"
                    data-testid="profile-website"
                  />
                </div>
              </div>
              
              {/* Key Contact Profile - Optional Section (Premium Feature) */}
              <div className="border-t border-zinc-700 pt-6 mt-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                      <User className="w-5 h-5 text-[#C41E3A]" />
                      Key Contact Profile
                      {!hasPaidTier && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 flex items-center gap-1">
                          <Lock className="w-3 h-3" /> Pro
                        </span>
                      )}
                    </h3>
                    <p className="text-zinc-400 text-sm">Add a detailed profile for your key contact person</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.include_key_contact}
                      onChange={(e) => {
                        if (!hasPaidTier && e.target.checked) {
                          requirePaidTier('Key Contact Profile');
                          return;
                        }
                        handleInputChange('include_key_contact', e.target.checked);
                      }}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-zinc-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#C41E3A]/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#C41E3A]"></div>
                    <span className="ml-2 text-sm text-zinc-400">{formData.include_key_contact ? 'Enabled' : 'Optional'}</span>
                  </label>
                </div>
                
                {formData.include_key_contact && (
                  <div className="space-y-4 bg-zinc-800/30 border border-zinc-700 rounded-lg p-4">
                    {/* Contact Photo */}
                    <div>
                      <label className="block text-zinc-300 text-sm font-medium mb-2">
                        Contact Photo
                      </label>
                      <div className="flex items-center gap-4">
                        {formData.key_contact.photo_url ? (
                          <div className="relative">
                            <img 
                              src={formData.key_contact.photo_url} 
                              alt="Contact" 
                              className="w-20 h-20 object-cover rounded-full border-2 border-zinc-600"
                            />
                            <button
                              type="button"
                              onClick={removeContactPhoto}
                              className="absolute -top-1 -right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-1"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <div className="w-20 h-20 bg-zinc-700 rounded-full flex items-center justify-center">
                            <User className="w-8 h-8 text-zinc-500" />
                          </div>
                        )}
                        <div>
                          <input
                            ref={contactPhotoInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleContactPhotoUpload}
                            className="hidden"
                            id="contact-photo-upload"
                          />
                          <label
                            htmlFor="contact-photo-upload"
                            className={`inline-flex items-center gap-2 bg-zinc-700 hover:bg-zinc-600 text-white px-4 py-2 rounded-lg text-sm cursor-pointer transition-colors ${isUploadingContactPhoto ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            {isUploadingContactPhoto ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Uploading...
                              </>
                            ) : (
                              <>
                                <Upload className="w-4 h-4" />
                                Upload Photo
                              </>
                            )}
                          </label>
                          <p className="text-zinc-500 text-xs mt-1">Professional headshot recommended</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Experience & Expertise */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-zinc-300 text-sm font-medium mb-2">
                          Years of Experience
                        </label>
                        <input
                          type="text"
                          value={formData.key_contact.years_experience}
                          onChange={(e) => updateKeyContact('years_experience', e.target.value)}
                          placeholder="e.g., 25+"
                          className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50"
                        />
                      </div>
                      <div>
                        <label className="block text-zinc-300 text-sm font-medium mb-2">
                          LinkedIn Profile
                        </label>
                        <input
                          type="url"
                          value={formData.key_contact.linkedin_url}
                          onChange={(e) => updateKeyContact('linkedin_url', e.target.value)}
                          placeholder="https://linkedin.com/in/..."
                          className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50"
                        />
                      </div>
                    </div>
                    
                    {/* Areas of Expertise */}
                    <div>
                      <label className="block text-zinc-300 text-sm font-medium mb-2">
                        Areas of Expertise
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {expertiseOptions.map(expertise => (
                          <button
                            key={expertise}
                            type="button"
                            onClick={() => toggleExpertise(expertise)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                              formData.key_contact.expertise_areas.includes(expertise)
                                ? 'bg-[#C41E3A] text-white'
                                : 'bg-zinc-700 text-zinc-400 hover:bg-zinc-600'
                            }`}
                          >
                            {expertise}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Notable Achievements */}
                    <div>
                      <label className="block text-zinc-300 text-sm font-medium mb-2">
                        Notable Achievements / Background
                      </label>
                      <textarea
                        value={formData.key_contact.achievements}
                        onChange={(e) => updateKeyContact('achievements', e.target.value)}
                        placeholder="e.g., Expanded operations to 15+ countries, Led $50M export growth, Key speaker at international trade forums..."
                        rows={2}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 resize-none"
                      />
                    </div>
                    
                    {/* AI-Generated Bio */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-zinc-300 text-sm font-medium">
                          Professional Introduction
                        </label>
                        <button
                          type="button"
                          onClick={generateContactBio}
                          disabled={isGeneratingContactBio || !formData.contact_name}
                          className="text-[#C41E3A] text-sm font-medium hover:underline flex items-center gap-1 disabled:opacity-50"
                        >
                          {isGeneratingContactBio ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Generating...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-4 h-4" />
                              Generate with AI
                            </>
                          )}
                        </button>
                      </div>
                      <textarea
                        value={formData.key_contact.bio_text}
                        onChange={(e) => updateKeyContact('bio_text', e.target.value)}
                        placeholder="A professional introduction paragraph will be generated based on the information above, or you can write your own..."
                        rows={3}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 resize-none"
                      />
                    </div>
                    
                    {/* Contact's Videos */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-zinc-300 text-sm font-medium">
                          Contact&apos;s Videos (Interviews, Talks)
                        </label>
                        {formData.key_contact.videos.length < 3 && (
                          <button
                            type="button"
                            onClick={addKeyContactVideo}
                            className="text-[#C41E3A] text-sm font-medium hover:underline"
                          >
                            + Add Video
                          </button>
                        )}
                      </div>
                      {formData.key_contact.videos.length === 0 ? (
                        <p className="text-zinc-500 text-sm">No videos added. These could be interviews, panel discussions, or presentations.</p>
                      ) : (
                        <div className="space-y-2">
                          {formData.key_contact.videos.map((video, index) => (
                            <div key={index} className="flex gap-2">
                              <input
                                type="url"
                                value={video.url}
                                onChange={(e) => updateKeyContactVideo(index, 'url', e.target.value)}
                                placeholder="YouTube URL"
                                className="flex-1 bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-white text-sm placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50"
                              />
                              <input
                                type="text"
                                value={video.title}
                                onChange={(e) => updateKeyContactVideo(index, 'title', e.target.value)}
                                placeholder="Video Title"
                                className="w-40 bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-white text-sm placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50"
                              />
                              <button
                                type="button"
                                onClick={() => removeKeyContactVideo(index)}
                                className="text-red-400 hover:text-red-300 px-2"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Step 4: Media */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Video className="w-5 h-5 text-[#C41E3A]" />
                Media & Videos
              </h2>
              
              <p className="text-zinc-400 text-sm">
                Add videos to make your profile stand out. YouTube links are recommended for easy embedding.
              </p>
              
              {/* Logo Upload/URL */}
              <div>
                <label className="block text-zinc-300 text-sm font-medium mb-2">
                  <Image className="w-4 h-4 inline mr-1" />
                  Company Logo
                </label>
                
                {/* Logo Preview */}
                {formData.logo_url && (
                  <div className="mb-3 relative inline-block">
                    <img 
                      src={formData.logo_url} 
                      alt="Logo preview" 
                      className="h-20 w-auto object-contain bg-white rounded-lg p-2"
                    />
                    <button
                      type="button"
                      onClick={removeLogo}
                      className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
                
                {/* Upload Options */}
                <div className="flex flex-col sm:flex-row gap-3">
                  {/* File Upload */}
                  <div className="flex-1">
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                      id="logo-upload"
                    />
                    <label
                      htmlFor="logo-upload"
                      className={`flex items-center justify-center gap-2 w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-zinc-300 cursor-pointer hover:bg-zinc-700 transition-colors ${isUploadingLogo ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {isUploadingLogo ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4" />
                          Upload Logo
                        </>
                      )}
                    </label>
                  </div>
                  
                  <div className="text-zinc-500 text-sm self-center">or</div>
                  
                  {/* URL Input */}
                  <div className="flex-1">
                    <input
                      type="url"
                      value={formData.logo_url}
                      onChange={(e) => handleInputChange('logo_url', e.target.value)}
                      placeholder="Paste logo URL"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A]"
                      data-testid="profile-logo"
                    />
                  </div>
                </div>
                <p className="text-zinc-500 text-xs mt-2">
                  Upload an image file or paste a direct URL. Recommended: PNG with transparent background.
                </p>
              </div>
              
              {/* Hero Video */}
              <div className="border border-zinc-700 rounded-lg p-4">
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <Video className="w-4 h-4 text-[#C41E3A]" />
                  Hero Video (Featured)
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-zinc-400 text-xs mb-1">Video URL</label>
                    <input
                      type="url"
                      value={formData.hero_video?.url || ''}
                      onChange={(e) => handleInputChange('hero_video', {
                        ...formData.hero_video,
                        type: 'youtube',
                        url: e.target.value
                      })}
                      placeholder="https://youtube.com/watch?v=..."
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50"
                      data-testid="profile-hero-video"
                    />
                  </div>
                  <div>
                    <label className="block text-zinc-400 text-xs mb-1">Video Title</label>
                    <input
                      type="text"
                      value={formData.hero_video?.title || ''}
                      onChange={(e) => handleInputChange('hero_video', {
                        ...formData.hero_video,
                        title: e.target.value
                      })}
                      placeholder="e.g., Company Overview"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50"
                    />
                  </div>
                </div>
                <p className="text-zinc-500 text-xs mt-3">
                  Adding your own video? <Link to="/video-guidelines" target="_blank" className="text-[#C41E3A] hover:underline">Review our video quality standards →</Link>
                </p>
              </div>
              
              {/* Additional Videos */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-white font-semibold flex items-center gap-2">
                    <Youtube className="w-4 h-4 text-[#C41E3A]" />
                    Additional Videos
                  </h3>
                  {formData.additional_videos.length < 5 && (
                    <button
                      type="button"
                      onClick={addVideoSlot}
                      className="text-[#C41E3A] text-sm font-medium hover:underline"
                    >
                      + Add Video
                    </button>
                  )}
                </div>
                
                {formData.additional_videos.length === 0 ? (
                  <div className="border-2 border-dashed border-zinc-700 rounded-lg p-6 text-center">
                    <Youtube className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
                    <p className="text-zinc-500 text-sm">No additional videos added</p>
                    <button
                      type="button"
                      onClick={addVideoSlot}
                      className="text-[#C41E3A] text-sm font-medium mt-2 hover:underline"
                    >
                      + Add your first video
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {formData.additional_videos.map((video, index) => (
                      <div key={index} className="border border-zinc-700 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-zinc-400 text-xs">Video {index + 1}</span>
                          <button
                            type="button"
                            onClick={() => removeVideo(index)}
                            className="text-red-400 text-xs hover:underline"
                          >
                            Remove
                          </button>
                        </div>
                        <div className="grid md:grid-cols-2 gap-3">
                          <input
                            type="url"
                            value={video.url}
                            onChange={(e) => updateVideo(index, 'url', e.target.value)}
                            placeholder="YouTube URL"
                            className="w-full bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-white text-sm placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50"
                          />
                          <input
                            type="text"
                            value={video.title}
                            onChange={(e) => updateVideo(index, 'title', e.target.value)}
                            placeholder="Video Title"
                            className="w-full bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-white text-sm placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Step 5: Preview */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Eye className="w-5 h-5 text-[#C41E3A]" />
                Preview & Publish
              </h2>
              
              {/* Audience Messages - PROMINENT */}
              {formData.audience_messages && formData.audience_messages.length > 0 && (
                <div className="bg-gradient-to-r from-[#C41E3A]/20 to-[#1E3A5F]/20 border border-[#C41E3A]/30 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <MessageSquare className="w-5 h-5 text-[#C41E3A]" />
                    <h3 className="text-white font-semibold">Messages for {formData.target_audience || 'Your Audience'}</h3>
                  </div>
                  <div className="space-y-2">
                    {formData.audience_messages.filter(Boolean).map((msg, idx) => (
                      <div key={idx} className="bg-zinc-900/50 rounded-lg p-3 border border-zinc-700">
                        <p className="text-zinc-200 text-sm italic">&ldquo;{msg}&rdquo;</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-zinc-500 text-xs mt-3">
                    AI-generated messages tailored to your target audience
                  </p>
                </div>
              )}
              
              {/* Structured Profile Sections */}
              {formData.profile_sections && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-white font-semibold">Profile Sections</h3>
                    <button
                      type="button"
                      onClick={generateProfileText}
                      disabled={isGenerating}
                      className="text-[#C41E3A] text-sm font-medium hover:underline flex items-center gap-1"
                    >
                      {isGenerating ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Regenerating...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4" />
                          Regenerate All
                        </>
                      )}
                    </button>
                  </div>
                  
                  {/* Section: Who We Are */}
                  {formData.profile_sections.who_we_are && (
                    <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4">
                      <h4 className="text-[#C41E3A] font-semibold text-sm mb-2 uppercase tracking-wide">Who We Are</h4>
                      <textarea
                        value={formData.profile_sections.who_we_are}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          profile_sections: { ...prev.profile_sections, who_we_are: e.target.value }
                        }))}
                        rows={2}
                        className="w-full bg-transparent text-zinc-200 text-sm leading-relaxed resize-none focus:outline-none"
                      />
                    </div>
                  )}
                  
                  {/* Section: Our Presence */}
                  {formData.profile_sections.our_presence && (
                    <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4">
                      <h4 className="text-[#C41E3A] font-semibold text-sm mb-2 uppercase tracking-wide">Our Presence</h4>
                      <textarea
                        value={formData.profile_sections.our_presence}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          profile_sections: { ...prev.profile_sections, our_presence: e.target.value }
                        }))}
                        rows={2}
                        className="w-full bg-transparent text-zinc-200 text-sm leading-relaxed resize-none focus:outline-none"
                      />
                    </div>
                  )}
                  
                  {/* Section: What We Seek */}
                  {formData.profile_sections.what_we_seek && (
                    <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4">
                      <h4 className="text-[#C41E3A] font-semibold text-sm mb-2 uppercase tracking-wide">What We&apos;re Looking For</h4>
                      <textarea
                        value={formData.profile_sections.what_we_seek}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          profile_sections: { ...prev.profile_sections, what_we_seek: e.target.value }
                        }))}
                        rows={2}
                        className="w-full bg-transparent text-zinc-200 text-sm leading-relaxed resize-none focus:outline-none"
                      />
                    </div>
                  )}
                  
                  {/* Section: Why Partner */}
                  {formData.profile_sections.why_partner && (
                    <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4">
                      <h4 className="text-[#C41E3A] font-semibold text-sm mb-2 uppercase tracking-wide">Why Partner With Us</h4>
                      <textarea
                        value={formData.profile_sections.why_partner}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          profile_sections: { ...prev.profile_sections, why_partner: e.target.value }
                        }))}
                        rows={2}
                        className="w-full bg-transparent text-zinc-200 text-sm leading-relaxed resize-none focus:outline-none"
                      />
                    </div>
                  )}
                  
                  {/* Section: Call to Action */}
                  {formData.profile_sections.call_to_action && (
                    <div className="bg-[#C41E3A]/10 border border-[#C41E3A]/30 rounded-lg p-4">
                      <h4 className="text-[#C41E3A] font-semibold text-sm mb-2 uppercase tracking-wide">Call to Action</h4>
                      <textarea
                        value={formData.profile_sections.call_to_action}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          profile_sections: { ...prev.profile_sections, call_to_action: e.target.value }
                        }))}
                        rows={2}
                        className="w-full bg-transparent text-zinc-200 text-sm leading-relaxed resize-none focus:outline-none"
                      />
                    </div>
                  )}
                </div>
              )}
              
              {/* Legacy single text (if no structured sections) */}
              {!formData.profile_sections && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-zinc-300 text-sm font-medium">
                      Profile Description
                    </label>
                    <button
                      type="button"
                      onClick={generateProfileText}
                      disabled={isGenerating}
                      className="text-[#C41E3A] text-sm font-medium hover:underline flex items-center gap-1"
                    >
                      {isGenerating ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4" />
                          Generate with AI
                        </>
                      )}
                    </button>
                  </div>
                  <textarea
                    value={formData.profile_text}
                    onChange={(e) => handleInputChange('profile_text', e.target.value)}
                    rows={6}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 focus:border-[#C41E3A] resize-none"
                    placeholder="Your company profile text will appear here..."
                    data-testid="profile-text"
                  />
                </div>
              )}
              
              {/* Preview Card */}
              <div className="border border-zinc-700 rounded-lg p-6 bg-zinc-800/50">
                <h3 className="text-lg font-bold text-white mb-4">Preview</h3>
                
                <div className="flex items-start gap-4 mb-4">
                  {formData.logo_url ? (
                    <img src={formData.logo_url} alt="Logo" className="w-16 h-16 object-contain rounded bg-white p-1" />
                  ) : (
                    <div className="w-16 h-16 bg-zinc-700 rounded flex items-center justify-center">
                      <Building2 className="w-8 h-8 text-zinc-500" />
                    </div>
                  )}
                  <div>
                    <h4 className="text-xl font-bold text-white">{formData.company_name || 'Company Name'}</h4>
                    {formData.tagline && <p className="text-zinc-400">{formData.tagline}</p>}
                    <p className="text-zinc-500 text-sm">
                      {[formData.headquarters, formData.year_established && `Since ${formData.year_established}`].filter(Boolean).join(' • ')}
                    </p>
                    {/* Profile Category Badge */}
                    <span className="inline-block mt-2 bg-[#1E3A5F]/30 text-[#6B9FD4] text-xs px-2 py-1 rounded">
                      {profileCategories.find(c => c.id === formData.profile_category)?.label || 'General Profile'}
                    </span>
                  </div>
                </div>
                
                {formData.certifications.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {formData.certifications.map(cert => (
                      <span key={cert} className="bg-[#1E3A5F]/30 text-[#6B9FD4] text-xs px-2 py-1 rounded">
                        {cert}
                      </span>
                    ))}
                  </div>
                )}
                
                {formData.looking_for && (
                  <div className="mt-4 pt-4 border-t border-zinc-700">
                    <p className="text-zinc-400 text-sm">
                      <span className="font-semibold text-white">Looking for:</span> {formData.looking_for}
                    </p>
                  </div>
                )}
              </div>
              
              {!isAdmin && pricing && addons.length > 0 && (
                <PlanReview
                  plan={plan} setPlan={setPlan} pricing={pricing} addons={addons}
                  declaration={publishDeclaration} setDeclaration={setPublishDeclaration}
                  terms={paymentTerms} setTerms={setPaymentTerms}
                  coupon={promoCode} setCoupon={setPromoCode}
                />
              )}
              
              {/* Attribution notice for admin */}
              {isAdmin && formData.created_on_behalf_of && (
                <div className="bg-[#1E3A5F]/20 border border-[#1E3A5F]/30 rounded-lg p-4">
                  <p className="text-[#6B9FD4] text-sm">
                    <Handshake className="w-4 h-4 inline mr-1" />
                    Profile will be attributed to: <strong>{formData.created_on_behalf_of}</strong>
                  </p>
                </div>
              )}
            </div>
          )}
          
          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-zinc-700">
            {currentStep > 0 ? (
              <button
                type="button"
                onClick={prevStep}
                className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
            ) : (
              <div />
            )}
            
            {currentStep < steps.length - 1 ? (
              <button
                type="button"
                onClick={nextStep}
                className="flex items-center gap-2 bg-[#C41E3A] hover:bg-[#A01830] text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                data-testid="profile-next-btn"
              >
                Next
                <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <div className="flex flex-wrap items-center justify-end gap-3">
                {!isAdmin && (
                  <button
                    type="button"
                    onClick={() => submitProfile(false)}
                    disabled={isSubmitting || !publishDeclaration}
                    className="px-5 py-3 rounded-lg border border-zinc-600 text-zinc-200 hover:border-zinc-400 disabled:opacity-50 font-medium"
                    data-testid="profile-save-draft-btn"
                  >
                    Save draft & pay later
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => submitProfile(!isAdmin)}
                  disabled={isSubmitting || (!isAdmin && (!publishDeclaration || !paymentTerms))}
                  className="flex items-center gap-2 bg-[#C41E3A] hover:bg-[#A01830] text-white px-6 py-3 rounded-lg font-semibold transition-colors disabled:opacity-50"
                  data-testid="profile-publish-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Share2 className="w-4 h-4" />
                      {isAdmin ? 'Publish Profile' : `Publish & Pay ₹${pricing ? Math.round(planTotal(plan, pricing, addons) * (1 + (pricing.gst_rate || 0) / 100)).toLocaleString('en-IN') : ''}`}
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
        
        {/* Footer note */}
        <p className="text-center text-zinc-500 text-xs mt-6">
          Your profile stays private until payment is complete. <button type="button" onClick={() => setJourney('wizard')} className="underline hover:text-white" data-testid="profile-view-plans-link">Review plans</button>
        </p>
        </>
        )}
      </div>
      
      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 max-w-md w-full my-8">
            {couponStatus === 'redeemed' ? (
              // Success state
              <div className="text-center py-8">
                <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-10 h-10 text-emerald-400" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">Welcome to Pro!</h3>
                <p className="text-zinc-400">Your profile has been upgraded. Refreshing...</p>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Crown className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Upgrade to Pro</h3>
                  <p className="text-zinc-400 text-sm">
                    Unlock <span className="text-amber-400 font-medium">{upgradeFeature}</span> and all premium features
                  </p>
                </div>
                
                <div className="bg-zinc-800/50 rounded-lg p-4 mb-6">
                  <p className="text-white font-semibold mb-3">Pro includes:</p>
                  <ul className="space-y-2">
                    {premiumFeatures.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-zinc-300">
                        <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* Pricing */}
                <div className="text-center mb-4">
                  {couponData && couponStatus === 'valid' ? (
                    <>
                      <div className="text-lg text-zinc-500 line-through">₹2,999</div>
                      <div className="text-3xl font-bold text-emerald-400">
                        {couponData.final_price === 0 ? 'FREE' : `₹${couponData.final_price}`}
                      </div>
                      <p className="text-emerald-400 text-sm">
                        {couponData.discount_type === 'free' ? 'Full discount applied!' : 
                         couponData.discount_type === 'percentage' ? `${couponData.discount_value}% off` :
                         `₹${couponData.discount_amount} off`}
                      </p>
                    </>
                  ) : (
                    <>
                      <div className="text-3xl font-bold text-white">₹2,999</div>
                      <p className="text-zinc-500 text-sm">one-time payment</p>
                    </>
                  )}
                </div>
                
                {/* Coupon Code Input */}
                <div className="mb-6">
                  <label className="block text-sm text-zinc-400 mb-2">Have a promo code?</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={couponCode}
                      onChange={(e) => {
                        setCouponCode(e.target.value.toUpperCase());
                        setCouponStatus(null);
                        setCouponError('');
                        setCouponData(null);
                      }}
                      placeholder="Enter code"
                      className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white uppercase placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                      disabled={couponStatus === 'validating'}
                    />
                    <button
                      onClick={validateCoupon}
                      disabled={!couponCode.trim() || couponStatus === 'validating'}
                      className="bg-zinc-700 hover:bg-zinc-600 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {couponStatus === 'validating' ? '...' : 'Apply'}
                    </button>
                  </div>
                  {couponError && (
                    <p className="text-red-400 text-sm mt-1">{couponError}</p>
                  )}
                  {couponStatus === 'valid' && couponData && (
                    <p className="text-emerald-400 text-sm mt-1 flex items-center gap-1">
                      <Check className="w-4 h-4" />
                      Coupon applied: {couponData.description || couponData.code}
                    </p>
                  )}
                </div>
                
                {/* Action Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowUpgradeModal(false);
                      setCouponCode('');
                      setCouponStatus(null);
                      setCouponData(null);
                      setCouponError('');
                    }}
                    className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white py-3 rounded-lg font-medium transition-colors"
                  >
                    Maybe Later
                  </button>
                  {couponData && couponData.final_price === 0 ? (
                    <button
                      onClick={redeemCoupon}
                      disabled={couponStatus === 'validating'}
                      className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white py-3 rounded-lg font-bold transition-colors disabled:opacity-50"
                    >
                      {couponStatus === 'validating' ? 'Processing...' : 'Claim Free Upgrade'}
                    </button>
                  ) : (
                    <button
                      onClick={() => navigate('/pricing')}
                      data-testid="upgrade-view-plans-btn"
                      className="flex-1 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white py-3 rounded-lg font-bold transition-colors"
                    >
                      View Plans &amp; Pay
                    </button>
                  )}
                </div>
                
                <p className="text-center text-zinc-500 text-xs mt-4">
                  Secure payment powered by Razorpay
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
